'use client';

import useSWR from 'swr';
import * as Sentry from '@sentry/nextjs';

import { SESSION_VALID_CHECK_URL } from '@content/navigation/navData';
import { IS_PRODUCTION } from '@lib/constants';

/**
 * Response shape of app.wistia.com/session/verify_valid. logged_in is
 * optional because disallowed origins receive an empty body.
 */
type SessionResponse = {
  logged_in?: boolean;
};

export type Session = {
  loggedIn: boolean;
};

export const toSession = (data?: SessionResponse): Session => ({
  loggedIn: data?.logged_in ?? false,
});

export const fetcher = async (url: string): Promise<SessionResponse> => {
  try {
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }
    return await res.json();
  } catch (error) {
    if (error instanceof TypeError) {
      // Network-layer failure (ad blocker, firewall, offline, CORS) — expected
      // at volume and unactionable, so log instead of creating an issue
      Sentry.logger.warn('Session check network failure', {
        component: 'useSession',
        message: error.message,
        online: navigator.onLine,
      });
    } else {
      // Contract violations: non-2xx status or unparseable body
      Sentry.captureException(error, {
        level: 'warning',
        tags: {
          component: 'useSession',
          action: 'fetcher',
        },
        extra: {
          online: navigator.onLine,
        },
      });
    }
    throw error;
  }
};

/**
 * Session state from the Rails app via the CORS-allowlisted verify_valid
 * endpoint. Fails closed: errors and non-production builds read as logged
 * out. See docs/spikes/20260720-logged-in-uid-from-rails.md.
 */
export const useSession = (): Session & { loading: boolean } => {
  const { data, error, isLoading } = useSWR<SessionResponse>(
    IS_PRODUCTION ? SESSION_VALID_CHECK_URL : null,
    fetcher,
    { shouldRetryOnError: false },
  );

  return {
    ...toSession(error ? undefined : data),
    loading: isLoading,
  };
};
